import { Link, useNavigate } from 'react-router';
import { ROUTES } from '@/constants';
import { useIssues } from '@/services/queries';
import { StatusPill } from '@/shared/ui/status-pill';
import { Skeleton } from '@/shared/ui/skeleton';

/**
 * The lab's open support issues, on the dashboard.
 *
 * Transcribed from the design: a matching 16px card, rows carrying the issue
 * reference in 10.5px mono with a status pill opposite and the subject
 * underneath, then a full-width outlined button to raise a new one.
 *
 * ── Why only the open ones ──────────────────────────────────────────────────
 *
 * The design's card is titled "Your open issues" and shows three. A dashboard
 * card is a prompt to act, and a resolved issue is not something to act on -
 * it belongs in the history on the Issues page. So this filters to what is
 * still live rather than showing the newest five whatever their state.
 *
 * ── Where the reference comes from ──────────────────────────────────────────
 *
 * The design writes "ISS-2871". We store a UUID and nothing else - there is no
 * sequence column - so the first eight characters are shown instead. It is
 * still the thing to quote at support, which is what the field is for. A
 * fabricated ISS-nnnn would be a number that matches nothing in the database.
 */

/** Five: enough to be a summary, few enough not to become the page. */
const SHOWN = 5;

/** The statuses that mean somebody is still waiting on something. */
const LIVE = new Set(['open', 'in_progress', 'waiting_on_user']);

export function OpenIssues() {
  const navigate = useNavigate();
  // Asked for more than are shown, because the filtering happens here: the
  // endpoint takes one status and "any of three" is not expressible in it.
  const query = useIssues({ limit: 25, offset: 0 });

  if (query.isPending) return <Skeleton className="h-64 w-full rounded-2xl" />;

  const open = (query.data?.data ?? []).filter((issue) => LIVE.has(issue.status)).slice(0, SHOWN);

  return (
    <div className="flex flex-col rounded-2xl border border-border bg-card px-7 py-[26px] shadow-[0_1px_3px_rgba(15,23,42,0.05)]">
      <h2 className="mb-4 text-[15px] font-bold whitespace-nowrap">Your open issues</h2>

      {open.length === 0 ? (
        <p className="flex-1 border-t border-border py-4 text-[12.5px] leading-relaxed text-muted-foreground">
          Nothing open. When something goes wrong, raise an issue here and attach a screenshot link
          - support replies in the same thread.
        </p>
      ) : (
        <div className="flex-1">
          {open.map((issue) => (
            <button
              key={issue.id}
              type="button"
              onClick={() => {
                void navigate(ROUTES.issues);
              }}
              className="w-full border-t border-border py-2.5 text-left hover:opacity-70"
            >
              <div className="flex items-center justify-between gap-2.5">
                <span className="font-mono text-[10.5px] text-muted-foreground">
                  {issue.id.slice(0, 8).toUpperCase()}
                </span>
                <StatusPill status={issue.status} minWidth={88} />
              </div>
              <div className="mt-1 text-[12.5px] leading-[1.4]">{issue.subject}</div>
            </button>
          ))}
        </div>
      )}

      <Link
        to={ROUTES.issues}
        className="mt-3.5 flex h-9 items-center justify-center rounded-lg border border-input text-[12.5px] font-semibold hover:border-primary"
      >
        Raise a new issue
      </Link>
    </div>
  );
}
